import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
  type ReactNode,
} from 'react';
import {
  TicketStatus,
  Priority,
  TicketType,
  IncidentCategory,
  WaitingReason,
  AssignmentGroup,
  isIncidentTicket,
  isRequestTicket,
  isProblemTicket,
  isChangeTicket,
} from '../types/ticket';
import type {
  Ticket,
  TicketFilters,
  AssignmentResult,
  Tech,
  JobTag,
  Location,
  IncidentSubcategory,
} from '../types/ticket';
import type { CreateTicketDTO, UpdateTicketDTO } from '../types/dto';
import type { TicketActivity, TicketAttachment } from '../types/ticketActivity';
import { ActivityType, ActivityVisibility, AuthorType } from '../types/ticketActivity';
import { Department } from '../types/knowledgeBase';
import { useUserStatus } from './UserStatusContext';
import { useAuth, isITStaff } from './AuthContext';
import { useTicketRepository, useTechRepository, useActivityRepository, useNotificationRepository } from '../data/hooks';
import { NotificationService } from '../services/NotificationService';

interface SnackbarState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'warning' | 'info';
}

interface TicketingContextValue {
  tickets: Ticket[];
  techs: Tech[];
  currentUserId: string;
  currentUser: Tech | undefined;
  filters: TicketFilters;
  snackbar: SnackbarState;
  reassignDrawerOpen: boolean;
  ticketToReassign: Ticket | null;
  isLoading: boolean;
  setFilters: (filters: TicketFilters) => void;
  handleJobCreated: (ticket: Ticket, assignment: AssignmentResult | null) => void;
  handleCreateTicket: (data: {
    ticketNumber: string;
    shortDescription: string;
    tags: JobTag[];
    priority: Priority;
    location: Location;
    notes?: string;
  }) => Promise<Ticket>;
  handleStartTicket: (ticketId: string) => void;
  handleHoldTicket: (ticketId: string, reason?: string) => void;
  handleCompleteTicket: (ticketId: string, notes?: string) => void;
  handleReopenTicket: (ticketId: string) => void;
  handleReassignTicket: (ticketId: string) => void;
  handleReassignSubmit: (
    ticketNumber: string,
    newTechId: string,
    comment: string,
    newPriority?: string
  ) => Promise<void>;
  handleChangePriority: (ticketId: string, newPriority?: string) => void;
  handleError: (message: string) => void;
  closeSnackbar: () => void;
  closeReassignDrawer: () => void;
  // Activity handlers
  handleAddComment: (ticketId: string, content: string, attachments?: TicketAttachment[]) => Promise<TicketActivity>;
  handleAddWorkNote: (ticketId: string, content: string) => Promise<TicketActivity>;
  handleStatusChange: (ticketId: string, newStatus: TicketStatus, options?: {
    waitingReason?: WaitingReason;
    waitingReasonNote?: string;
    resolutionNotes?: string;
  }) => Promise<void>;
  handleEscalateTicket: (ticketId: string, newGroup: AssignmentGroup, reason: string) => Promise<void>;
  handleAskQuestion: (ticketId: string, questionText: string) => Promise<TicketActivity>;
  handleAssignTicket: (ticketId: string, targetTechId: string) => Promise<Ticket>;
  getTicketActivities: (ticketId: string) => Promise<TicketActivity[]>;
}

const TicketingContext = createContext<TicketingContextValue | undefined>(undefined);

interface TicketingProviderProps {
  children: ReactNode;
}

export function TicketingProvider({ children }: TicketingProviderProps) {
  const { activeJob, stopJob } = useUserStatus();
  const { user: authUser, getUserId, getTechId } = useAuth();
  const ticketRepo = useTicketRepository();
  const techRepo = useTechRepository();
  const activityRepo = useActivityRepository();
  const notificationRepo = useNotificationRepository();

  // Create notification service instance (stable reference)
  const notificationServiceRef = useRef<NotificationService | null>(null);
  if (!notificationServiceRef.current) {
    notificationServiceRef.current = new NotificationService(notificationRepo);
  }
  const notificationService = notificationServiceRef.current;

  // Get current user ID from auth context
  const currentUserId = authUser?.id ?? '';
  // Get tech ID for assignment operations (null for non-IT users)
  const currentTechId = getTechId();
  // Determine author type based on user type
  const currentAuthorType = authUser && isITStaff(authUser) ? AuthorType.IT_STAFF : AuthorType.USER;

  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [techs, setTechs] = useState<Tech[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters] = useState<TicketFilters>({
    priorities: [],
    statuses: [],
    locations: [],
    ticketTypes: [],
    categories: [],
    searchQuery: '',
    tags: [],
  });

  const [reassignDrawerOpen, setReassignDrawerOpen] = useState(false);
  const [ticketToReassign, setTicketToReassign] = useState<Ticket | null>(null);
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Current user's tech record (for IT staff only)
  const currentUser = currentTechId ? techs.find((t) => t.id === currentTechId) : undefined;

  // Get display name from auth user or tech record
  const currentUserName = authUser?.name ?? currentUser?.name ?? 'Unknown User';

  // Load data from repositories on mount
  useEffect(() => {
    let mounted = true;

    async function loadData() {
      try {
        const [loadedTickets, loadedTechs] = await Promise.all([
          ticketRepo.getAll(),
          techRepo.getAll(),
        ]);
        if (mounted) {
          setTickets(loadedTickets);
          setTechs(loadedTechs);
          setIsLoading(false);
        }
      } catch (error) {
        console.error('Failed to load ticketing data:', error);
        if (mounted) {
          setIsLoading(false);
        }
      }
    }

    loadData();

    return () => {
      mounted = false;
    };
  }, [ticketRepo, techRepo]);

  const handleJobCreated = useCallback(
    (ticket: Ticket, assignment: AssignmentResult | null) => {
      // Optimistic update
      setTickets((prev) => [ticket, ...prev]);

      // Build the appropriate DTO based on ticket type
      const buildCreateDTO = (t: Ticket): CreateTicketDTO => {
        const baseFields = {
          ticketNumber: t.ticketNumber,
          shortDescription: t.shortDescription,
          priority: t.priority,
          location: t.location,
          requesterId: t.requesterId,
          requesterName: t.requesterName,
          requesterEmail: t.requesterEmail,
          requesterDepartment: t.requesterDepartment,
          createdBy: t.createdBy,
          createdByName: t.createdByName,
          notes: t.notes,
          tags: t.tags,
        };

        if (isIncidentTicket(t)) {
          return {
            ...baseFields,
            ticketType: TicketType.INCIDENT,
            category: t.category,
            subcategory: t.subcategory,
          };
        } else if (isRequestTicket(t)) {
          return {
            ...baseFields,
            ticketType: TicketType.REQUEST,
            category: t.category,
            subcategory: t.subcategory,
            approvalStatus: t.approvalStatus,
            neededByDate: t.neededByDate,
            requestedItemName: t.requestedItemName,
          };
        } else if (isProblemTicket(t)) {
          return {
            ...baseFields,
            ticketType: TicketType.PROBLEM,
            category: t.category,
            subcategory: t.subcategory,
            problemStatement: t.problemStatement,
            rootCause: t.rootCause,
            workaround: t.workaround,
            linkedIncidentIds: t.linkedIncidentIds,
          };
        } else {
          // Must be ChangeTicket since we've exhausted all other types
          const changeTicket = t;
          return {
            ...baseFields,
            ticketType: TicketType.CHANGE,
            changeCategory: changeTicket.changeCategory,
            changeType: changeTicket.changeType,
            riskLevel: changeTicket.riskLevel,
            approvalStatus: changeTicket.approvalStatus,
            rollbackPlan: changeTicket.rollbackPlan,
            implementationStart: changeTicket.implementationStart,
            implementationEnd: changeTicket.implementationEnd,
          };
        }
      };

      // Persist to database
      ticketRepo
        .create(buildCreateDTO(ticket))
        .then((persisted) => {
          // Update the ticket with persisted ID and assign
          setTickets((prev) => prev.map((t) => (t.id === ticket.id ? persisted : t)));

          // Assign to tech if assignment provided
          if (assignment?.techId) {
            ticketRepo.assignToTech(persisted.id, assignment.techId).catch(console.error);
          }
        })
        .catch((error) => {
          console.error('Failed to persist ticket:', error);
          setTickets((prev) => prev.filter((t) => t.id !== ticket.id));
        });

      console.log(
        'Job created:',
        ticket.ticketNumber,
        assignment ? `assigned to: ${assignment.techName}` : '(unassigned)'
      );
    },
    [ticketRepo]
  );

  const handleCreateTicket = useCallback(
    async (data: {
      ticketNumber: string;
      shortDescription: string;
      tags: JobTag[];
      priority: Priority;
      location: Location;
      notes?: string;
    }): Promise<Ticket> => {
      // Legacy triage view uses this method - provide defaults for new required fields
      const ticket = await ticketRepo.create({
        ticketNumber: data.ticketNumber,
        shortDescription: data.shortDescription,
        ticketType: TicketType.INCIDENT,
        category: IncidentCategory.OTHER,
        subcategory: 'General' as IncidentSubcategory,
        priority: data.priority,
        location: data.location,
        requesterId: currentUserId,
        requesterName: 'Triage User',
        requesterEmail: 'triage@percepta.com',
        requesterDepartment: Department.IT,
        createdBy: currentUserId,
        createdByName: 'Triage User',
        notes: data.notes,
        tags: data.tags,
      });

      setTickets((prev) => [ticket, ...prev]);
      return ticket;
    },
    [ticketRepo, currentUserId]
  );

  const handleStartTicket = useCallback(
    (ticketId: string) => {
      // Optimistic update
      setTickets((prev) =>
        prev.map((t) =>
          t.id === ticketId
            ? ({ ...t, status: TicketStatus.IN_PROGRESS, updatedAt: new Date() } as Ticket)
            : t
        )
      );

      // Persist to database
      ticketRepo.updateStatus(ticketId, TicketStatus.IN_PROGRESS).catch((error) => {
        console.error('Failed to start ticket:', error);
      });
    },
    [ticketRepo]
  );

  const handleHoldTicket = useCallback(
    (ticketId: string, reason?: string) => {
      const ticket = tickets.find((t) => t.id === ticketId);

      // Optimistic update
      setTickets((prev) =>
        prev.map((t) =>
          t.id === ticketId ? ({ ...t, status: TicketStatus.WAITING, updatedAt: new Date() } as Ticket) : t
        )
      );

      setSnackbar({
        open: true,
        message: `Ticket ${ticket?.ticketNumber} set to waiting${reason ? ': ' + reason : ''}`,
        severity: 'info',
      });

      // Persist to database
      ticketRepo.updateStatus(ticketId, TicketStatus.WAITING).catch((error) => {
        console.error('Failed to set ticket to waiting:', error);
      });
    },
    [tickets, ticketRepo]
  );

  const handleCompleteTicket = useCallback(
    (ticketId: string, _notes?: string) => {
      const ticket = tickets.find((t) => t.id === ticketId);

      if (activeJob?.ticketId === ticketId) stopJob();

      // Optimistic update
      setTickets((prev) =>
        prev.map((t) =>
          t.id === ticketId ? ({ ...t, status: TicketStatus.RESOLVED, updatedAt: new Date() } as Ticket) : t
        )
      );

      setSnackbar({
        open: true,
        message: `Ticket ${ticket?.ticketNumber} marked as complete!`,
        severity: 'success',
      });

      // Persist to database
      ticketRepo.updateStatus(ticketId, TicketStatus.RESOLVED).catch((error) => {
        console.error('Failed to complete ticket:', error);
      });

      // Let the exit animation play before removing from local state
      setTimeout(() => setTickets((prev) => prev.filter((t) => t.id !== ticketId)), 600);

      // Delete from database after animation
      setTimeout(() => {
        ticketRepo.delete(ticketId).catch((error) => {
          console.error('Failed to delete completed ticket:', error);
        });
      }, 700);
    },
    [tickets, activeJob, stopJob, ticketRepo]
  );

  const handleReopenTicket = useCallback(
    (ticketId: string) => {
      const ticket = tickets.find((t) => t.id === ticketId);

      // Optimistic update
      setTickets((prev) =>
        prev.map((t) =>
          t.id === ticketId ? ({ ...t, status: TicketStatus.NEW, updatedAt: new Date() } as Ticket) : t
        )
      );

      setSnackbar({
        open: true,
        message: `Ticket ${ticket?.ticketNumber} reopened`,
        severity: 'info',
      });

      // Persist to database
      ticketRepo.updateStatus(ticketId, TicketStatus.NEW).catch((error) => {
        console.error('Failed to reopen ticket:', error);
      });
    },
    [tickets, ticketRepo]
  );

  const handleReassignTicket = useCallback((ticketId: string) => {
    setTickets((prev) => {
      const ticket = prev.find((t) => t.id === ticketId);
      if (ticket) {
        setTicketToReassign(ticket);
        setReassignDrawerOpen(true);
      }
      return prev;
    });
  }, []);

  const handleReassignSubmit = useCallback(
    async (ticketNumber: string, newTechId: string, _comment: string, newPriority?: string) => {
      const ticket = tickets.find((t) => t.ticketNumber === ticketNumber);
      if (!ticket) return;

      if (activeJob?.ticketId === ticket.id) stopJob();

      const targetTech = techs.find((t) => t.id === newTechId);
      const targetTechName = targetTech?.name ?? 'Unknown';
      const previousTechId = ticket.assignedTechId;
      const previousTechName = previousTechId
        ? techs.find((t) => t.id === previousTechId)?.name ?? null
        : null;

      // Mark as resolved to trigger exit animation in queue views
      setTickets((prev) =>
        prev.map((t) =>
          t.ticketNumber === ticketNumber
            ? ({ ...t, status: TicketStatus.RESOLVED, updatedAt: new Date() } as Ticket)
            : t
        )
      );

      setSnackbar({
        open: true,
        message: `Ticket ${ticketNumber} reassigned to ${targetTechName}${newPriority ? ` (${newPriority})` : ''}`,
        severity: 'success',
      });

      setReassignDrawerOpen(false);
      setTicketToReassign(null);

      // Update ticket assignment in database
      try {
        await ticketRepo.update(ticket.id, {
          assignedTechId: newTechId,
          assignedTechName: targetTechName,
        });

        // Create assignment activity for audit trail
        const isReassignment = previousTechId !== null;
        const displayMessage = isReassignment
          ? `Reassigned from ${previousTechName ?? 'Unassigned'} to ${targetTechName}`
          : `Assigned to ${targetTechName}`;

        const activity = await activityRepo.create({
          ticketId: ticket.id,
          type: ActivityType.ASSIGNED,
          visibility: ActivityVisibility.PUBLIC,
          authorId: currentUserId,
          authorName: currentUserName,
          authorType: currentAuthorType,
          previousAssignedTechId: previousTechId ?? undefined,
          previousAssignedTechName: previousTechName ?? undefined,
          newAssignedTechId: newTechId,
          newAssignedTechName: targetTechName,
          displayMessage,
          triggersNotification: true,
        });

        // Process notification
        notificationService.processActivity(activity, ticket).catch(console.error);
      } catch (error) {
        console.error('Failed to reassign ticket:', error);
      }

      // Remove from queue view after animation
      setTimeout(
        () => setTickets((prev) => prev.filter((t) => t.ticketNumber !== ticketNumber)),
        600
      );
    },
    [tickets, techs, activeJob, stopJob, ticketRepo, activityRepo, currentUserId, currentUserName, currentAuthorType, notificationService]
  );

  const handleChangePriority = useCallback((_ticketId: string, _newPriority?: string) => {
    setSnackbar({
      open: true,
      message: 'Priority change feature coming soon',
      severity: 'info',
    });
  }, []);

  const handleError = useCallback((message: string) => {
    setSnackbar({
      open: true,
      message,
      severity: 'error',
    });
  }, []);

  const closeSnackbar = useCallback(() => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  }, []);

  const closeReassignDrawer = useCallback(() => {
    setReassignDrawerOpen(false);
    setTicketToReassign(null);
  }, []);

  // Activity handlers
  const handleAddComment = useCallback(
    async (ticketId: string, content: string, attachments?: TicketAttachment[]): Promise<TicketActivity> => {
      const ticket = tickets.find((t) => t.id === ticketId);

      const activity = await activityRepo.create({
        ticketId,
        type: ActivityType.COMMENT,
        visibility: ActivityVisibility.PUBLIC,
        authorId: currentUserId,
        authorName: currentUserName,
        authorType: currentAuthorType,
        content,
        attachments,
        triggersNotification: true,
      });

      // Process notification
      if (ticket) {
        notificationService.processActivity(activity, ticket).catch(console.error);
      }

      return activity;
    },
    [activityRepo, currentUserId, currentUserName, currentAuthorType, tickets, notificationService]
  );

  const handleAddWorkNote = useCallback(
    async (ticketId: string, content: string): Promise<TicketActivity> => {
      const activity = await activityRepo.create({
        ticketId,
        type: ActivityType.WORK_NOTE,
        visibility: ActivityVisibility.INTERNAL,
        authorId: currentUserId,
        authorName: currentUserName,
        authorType: currentAuthorType,
        content,
        triggersNotification: false,
      });

      return activity;
    },
    [activityRepo, currentUserId, currentUserName, currentAuthorType]
  );

  const handleStatusChange = useCallback(
    async (
      ticketId: string,
      newStatus: TicketStatus,
      options?: {
        waitingReason?: WaitingReason;
        waitingReasonNote?: string;
        resolutionNotes?: string;
      }
    ): Promise<void> => {
      const ticket = tickets.find((t) => t.id === ticketId);
      if (!ticket) return;

      const previousStatus = ticket.status;

      // Update ticket
      const updateData: Record<string, unknown> = { status: newStatus };
      if (options?.waitingReason) {
        updateData.waitingReason = options.waitingReason;
        updateData.waitingReasonNote = options.waitingReasonNote;
      }
      if (options?.resolutionNotes) {
        updateData.resolutionNotes = options.resolutionNotes;
        updateData.resolvedAt = new Date();
      }
      if (newStatus === TicketStatus.CLOSED) {
        updateData.closedAt = new Date();
      }

      // Optimistic update
      setTickets((prev) =>
        prev.map((t) =>
          t.id === ticketId
            ? ({ ...t, ...updateData, updatedAt: new Date() } as Ticket)
            : t
        )
      );

      // Persist ticket update
      await ticketRepo.update(ticketId, updateData as UpdateTicketDTO);

      // Create activity
      const activity = await activityRepo.create({
        ticketId,
        type: newStatus === TicketStatus.RESOLVED ? ActivityType.RESOLVED : ActivityType.STATUS_CHANGE,
        visibility: ActivityVisibility.PUBLIC,
        authorId: currentUserId,
        authorName: currentUserName,
        authorType: currentAuthorType,
        previousStatus,
        newStatus,
        waitingReason: options?.waitingReason,
        waitingReasonNote: options?.waitingReasonNote,
        displayMessage: newStatus === TicketStatus.RESOLVED
          ? 'Ticket resolved'
          : `Status changed from ${previousStatus} to ${newStatus}`,
        triggersNotification: true,
      });

      // Process notification (use updated ticket data)
      const updatedTicket = { ...ticket, ...updateData, status: newStatus };
      notificationService.processActivity(activity, updatedTicket as Ticket).catch(console.error);

      setSnackbar({
        open: true,
        message: `Ticket status changed to ${newStatus}`,
        severity: 'success',
      });
    },
    [tickets, ticketRepo, activityRepo, currentUserId, currentUserName, currentAuthorType, notificationService]
  );

  const handleEscalateTicket = useCallback(
    async (
      ticketId: string,
      newGroup: AssignmentGroup,
      reason: string
    ): Promise<void> => {
      const ticket = tickets.find((t) => t.id === ticketId);
      if (!ticket) return;

      const previousGroup = ticket.assignmentGroup ?? AssignmentGroup.SERVICE_DESK;
      const previousTechId = ticket.assignedTechId;
      const previousTechName = previousTechId
        ? techs.find((t) => t.id === previousTechId)?.name ?? null
        : null;

      // Escalation transfers to a new team - unassign current tech
      // The new team will assign someone from their group
      const ticketUpdates: Partial<Ticket> = {
        assignmentGroup: newGroup,
        assignedTechId: null,
      };

      // Optimistic update
      setTickets((prev) =>
        prev.map((t) =>
          t.id === ticketId
            ? ({ ...t, ...ticketUpdates, updatedAt: new Date() } as Ticket)
            : t
        )
      );

      // Persist update
      await ticketRepo.update(ticketId, ticketUpdates as UpdateTicketDTO);

      // Create detailed escalation activity
      const displayMessage = previousTechName
        ? `Escalated from ${previousGroup} (${previousTechName}) to ${newGroup}`
        : `Escalated from ${previousGroup} to ${newGroup}`;

      const activity = await activityRepo.create({
        ticketId,
        type: ActivityType.ESCALATED,
        visibility: ActivityVisibility.PUBLIC,
        authorId: currentUserId,
        authorName: currentUserName,
        authorType: currentAuthorType,
        previousGroup,
        newGroup,
        escalationReason: reason,
        displayMessage,
        triggersNotification: true,
      });

      // Create escalation notification for the new team
      notificationService
        .createEscalationNotification(activity, ticket, newGroup)
        .catch(console.error);

      setSnackbar({
        open: true,
        message: `Ticket escalated to ${newGroup}`,
        severity: 'success',
      });
    },
    [tickets, techs, ticketRepo, activityRepo, currentUserId, currentUserName, currentAuthorType, notificationService]
  );

  const handleAskQuestion = useCallback(
    async (ticketId: string, questionText: string): Promise<TicketActivity> => {
      const ticket = tickets.find((t) => t.id === ticketId);
      if (!ticket) {
        throw new Error('Ticket not found');
      }

      // Create the question activity
      const activity = await activityRepo.create({
        ticketId,
        type: ActivityType.QUESTION_TO_USER,
        visibility: ActivityVisibility.PUBLIC,
        authorId: currentUserId,
        authorName: currentUserName,
        authorType: currentAuthorType,
        questionText,
        displayMessage: questionText,
        triggersNotification: true,
        isAnswered: false,
      });

      // Process notification for the question
      notificationService.processActivity(activity, ticket).catch(console.error);

      // Change ticket status to Waiting on User
      const previousStatus = ticket.status;
      await ticketRepo.update(ticketId, {
        status: TicketStatus.WAITING,
        waitingReason: WaitingReason.USER,
        waitingReasonNote: 'Question pending response',
      });

      // Optimistic update for status
      setTickets((prev) =>
        prev.map((t) =>
          t.id === ticketId
            ? {
                ...t,
                status: TicketStatus.WAITING,
                waitingReason: WaitingReason.USER,
                waitingReasonNote: 'Question pending response',
                updatedAt: new Date(),
              }
            : t
        )
      );

      // Create status change activity (no notification for this automatic status change)
      await activityRepo.create({
        ticketId,
        type: ActivityType.STATUS_CHANGE,
        visibility: ActivityVisibility.PUBLIC,
        authorId: currentUserId,
        authorName: currentUserName,
        authorType: currentAuthorType,
        previousStatus,
        newStatus: TicketStatus.WAITING,
        waitingReason: WaitingReason.USER,
        waitingReasonNote: 'Question pending response',
        displayMessage: 'Waiting for user response',
        triggersNotification: false,
      });

      setSnackbar({
        open: true,
        message: 'Question sent to user',
        severity: 'success',
      });

      return activity;
    },
    [tickets, ticketRepo, activityRepo, currentUserId, currentUserName, currentAuthorType, notificationService]
  );

  const handleAssignTicket = useCallback(
    async (ticketId: string, targetTechId: string): Promise<Ticket> => {
      // Try to find ticket in local state first, otherwise load from repository
      let ticket = tickets.find((t) => t.id === ticketId);
      if (!ticket) {
        const loadedTicket = await ticketRepo.getById(ticketId);
        if (!loadedTicket) {
          throw new Error('Ticket not found');
        }
        ticket = loadedTicket;
      }

      // Try to find tech in local state first, otherwise load from repository
      let targetTech = techs.find((t) => t.id === targetTechId);
      if (!targetTech) {
        const loadedTech = await techRepo.getById(targetTechId);
        if (!loadedTech) {
          throw new Error(`Tech not found: ${targetTechId}`);
        }
        targetTech = loadedTech;
      }

      const previousTechId = ticket.assignedTechId;
      const previousTechName = previousTechId
        ? techs.find((t) => t.id === previousTechId)?.name ?? null
        : null;

      // Determine if this is initial assignment or reassignment
      const isReassignment = previousTechId !== null && previousTechId !== targetTechId;
      const isInitialAssignment = previousTechId === null;

      // Prepare ticket updates
      const ticketUpdates: Record<string, unknown> = {
        assignedTechId: targetTechId,
        assignedTechName: targetTech.name,
      };

      // Set status to IN_PROGRESS if currently NEW or WAITING
      const shouldUpdateStatus =
        ticket.status === TicketStatus.NEW || ticket.status === TicketStatus.WAITING;
      if (shouldUpdateStatus) {
        ticketUpdates.status = TicketStatus.IN_PROGRESS;
      }

      // Optimistic update
      setTickets((prev) =>
        prev.map((t) =>
          t.id === ticketId
            ? ({ ...t, ...ticketUpdates, updatedAt: new Date() } as Ticket)
            : t
        )
      );

      // Persist ticket update
      const updatedTicket = await ticketRepo.update(
        ticketId,
        ticketUpdates as UpdateTicketDTO
      );

      if (!updatedTicket) {
        throw new Error('Failed to update ticket');
      }

      // Create assignment activity
      const displayMessage = isInitialAssignment
        ? `Assigned to ${targetTech.name}`
        : `Reassigned from ${previousTechName ?? 'Unassigned'} to ${targetTech.name}`;

      const activity = await activityRepo.create({
        ticketId,
        type: ActivityType.ASSIGNED,
        visibility: ActivityVisibility.PUBLIC,
        authorId: currentUserId,
        authorName: currentUserName,
        authorType: currentAuthorType,
        previousAssignedTechId: previousTechId ?? undefined,
        previousAssignedTechName: previousTechName ?? undefined,
        newAssignedTechId: targetTechId,
        newAssignedTechName: targetTech.name,
        displayMessage,
        triggersNotification: true,
      });

      // If status also changed, create a status change activity
      if (shouldUpdateStatus) {
        await activityRepo.create({
          ticketId,
          type: ActivityType.STATUS_CHANGE,
          visibility: ActivityVisibility.PUBLIC,
          authorId: currentUserId,
          authorName: currentUserName,
          authorType: currentAuthorType,
          previousStatus: ticket.status,
          newStatus: TicketStatus.IN_PROGRESS,
          displayMessage: `Status changed from ${ticket.status} to In Progress`,
          triggersNotification: false, // Assignment already triggers notification
        });
      }

      // Process notification for the assignment
      notificationService.processActivity(activity, updatedTicket).catch(console.error);

      // Show success message
      const actionVerb = isInitialAssignment ? 'Assigned' : 'Reassigned';
      setSnackbar({
        open: true,
        message: `Ticket ${actionVerb.toLowerCase()} to ${targetTech.name}`,
        severity: 'success',
      });

      return updatedTicket;
    },
    [tickets, techs, ticketRepo, techRepo, activityRepo, currentUserId, currentUserName, currentAuthorType, notificationService]
  );

  const getTicketActivities = useCallback(
    async (ticketId: string): Promise<TicketActivity[]> => {
      return activityRepo.getByTicketId(ticketId);
    },
    [activityRepo]
  );

  const contextValue = useMemo<TicketingContextValue>(
    () => ({
      tickets,
      techs,
      currentUserId,
      currentUser,
      filters,
      snackbar,
      reassignDrawerOpen,
      ticketToReassign,
      isLoading,
      setFilters,
      handleJobCreated,
      handleCreateTicket,
      handleStartTicket,
      handleHoldTicket,
      handleCompleteTicket,
      handleReopenTicket,
      handleReassignTicket,
      handleReassignSubmit,
      handleChangePriority,
      handleError,
      closeSnackbar,
      closeReassignDrawer,
      handleAddComment,
      handleAddWorkNote,
      handleStatusChange,
      handleEscalateTicket,
      handleAskQuestion,
      handleAssignTicket,
      getTicketActivities,
    }),
    [
      tickets,
      techs,
      currentUserId,
      currentUser,
      filters,
      snackbar,
      reassignDrawerOpen,
      ticketToReassign,
      isLoading,
      handleJobCreated,
      handleCreateTicket,
      handleStartTicket,
      handleHoldTicket,
      handleCompleteTicket,
      handleReopenTicket,
      handleReassignTicket,
      handleReassignSubmit,
      handleChangePriority,
      handleError,
      closeSnackbar,
      closeReassignDrawer,
      handleAddComment,
      handleAddWorkNote,
      handleStatusChange,
      handleEscalateTicket,
      handleAskQuestion,
      handleAssignTicket,
      getTicketActivities,
    ]
  );

  // Show nothing while loading (RepositoryProvider handles the main loading screen)
  if (isLoading) {
    return null;
  }

  return <TicketingContext.Provider value={contextValue}>{children}</TicketingContext.Provider>;
}

export function useTicketing(): TicketingContextValue {
  const ctx = useContext(TicketingContext);
  if (!ctx) {
    throw new Error('useTicketing must be used within TicketingProvider');
  }
  return ctx;
}
